import { generateConfigScript } from '@spro-front/widget';

import { WIDGET_SCRIPT_URL } from 'shared/entities/domain';

const CONFIG_SCRIPT_ID = 'smartbot-widget-config';
const INJECT_SCRIPT_ID = 'smartbot-widget-inject';
const WIDGET_IFRAME_ID = 'smartbot-live-chat-iframe';

type WidgetScriptRemovalOptions = {
  clearSessionToken?: boolean;
};

type ScriptDescriptor = {
  attributes: Record<string, string | boolean>;
  content?: string;
};

function getWidgetScriptDescriptors(apiKey: string): {
  config: ScriptDescriptor;
  inject: ScriptDescriptor;
} {
  return {
    config: {
      attributes: { id: CONFIG_SCRIPT_ID, type: 'text/javascript' },
      content: generateConfigScript({ apiKey })
    },
    inject: {
      attributes: {
        id: INJECT_SCRIPT_ID,
        type: 'text/javascript',
        async: true,
        src: WIDGET_SCRIPT_URL,
        onload: 'window.__smartbotOnLoad && window.__smartbotOnLoad()'
      }
    }
  };
}

function descriptorToHTML(descriptor: ScriptDescriptor): string {
  const attrs = Object.entries(descriptor.attributes)
    .map(([key, value]) => (value === true ? key : `${key}="${value}"`))
    .join(' ');

  return `<script ${attrs}>${descriptor.content ?? ''}</script>`;
}

function descriptorToElement(descriptor: ScriptDescriptor): HTMLScriptElement {
  const script = document.createElement('script');

  for (const [key, value] of Object.entries(descriptor.attributes)) {
    if (typeof value === 'boolean') {
      if (value) {
        script.setAttribute(key, '');
      }
    } else {
      script.setAttribute(key, value);
    }
  }

  if (descriptor.content) {
    script.text = descriptor.content;
  }

  return script;
}

export function generateWidgetSnippet(apiKey: string): string {
  const { config, inject } = getWidgetScriptDescriptors(apiKey);

  return `${descriptorToHTML(config)}\n${descriptorToHTML(inject)}`;
}

// Инжектим скрипт виджета на страницу
export function injectWidgetScript(
  apiKey: string,
  removalOptions?: WidgetScriptRemovalOptions
): Promise<void> {
  removeWidgetScript(removalOptions);

  const { config, inject } = getWidgetScriptDescriptors(apiKey);

  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const settle = (error?: Error): void => {
      if (settled) {
        return;
      }

      settled = true;
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };

    const configElement = descriptorToElement(config);
    document.body.appendChild(configElement);

    window.__smartbotOnLoad = (): void => {
      const widgetInit = window.smartbotWidgetInit;
      if (!widgetInit) {
        settle(new Error('Widget init function is unavailable'));

        return;
      }

      void widgetInit(
        { apiKey },
        () => settle(),
        () => settle(new Error('Widget initialization failed'))
      ).catch((error: unknown) => {
        settle(
          error instanceof Error
            ? error
            : new Error('Widget initialization failed')
        );
      });
    };

    const injectElement = descriptorToElement(inject);
    injectElement.addEventListener(
      'error',
      () => settle(new Error('Widget script failed to load')),
      { once: true }
    );
    document.body.appendChild(injectElement);
  });
}

export function removeWidgetScript(
  options: WidgetScriptRemovalOptions = {}
): void {
  try {
    window.smartbotWidgetRemove?.({
      clearSessionToken: options.clearSessionToken
    });
  } catch {
    // виджет мог быть в несогласованном состоянии
  }

  document.getElementById(CONFIG_SCRIPT_ID)?.remove();
  document.getElementById(INJECT_SCRIPT_ID)?.remove();

  document.getElementById(WIDGET_IFRAME_ID)?.remove();

  window.smartbot = [];
  delete window.smartbotWidgetInit;
  delete window.smartbotWidgetRemove;
  delete window.__smartbotOnLoad;
  delete window.__smartbotInternal;
}
